import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/payFileRemoveApproval.mutation"

import type {
  PayFileRemoveApprovalFailure,
  PayFileRemoveApprovalInput,
  RemovePayFileApprovalMutation,
  RemovePayFileApprovalMutationVariables,
} from "@/graphql/codegen/graphql"
import { RemovePayFileApprovalDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseRemovePayFileApprovalReturn {
  removePayFileApproval: (input: PayFileRemoveApprovalInput) => Promise<boolean>
  isRemovingApproval: boolean
}

export const useRemovePayFileApproval = (): UseRemovePayFileApprovalReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    RemovePayFileApprovalMutation,
    RemovePayFileApprovalMutationVariables
  >(RemovePayFileApprovalDocument)

  const removePayFileApproval = useCallback(
    async (input: PayFileRemoveApprovalInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.payFile.removeApproval
      const hasError = error || output?.__typename === "PayFileRemoveApprovalFailure"

      if (hasError) {
        const message = error?.message ?? (output as PayFileRemoveApprovalFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }

      notify({ type: NotificationType.Success, message: "Approval removed" })
      return true
    },
    [executeMutation, notify]
  )

  return { removePayFileApproval, isRemovingApproval: fetching }
}
